import Dropdown from '@/componentsDS/Dropdown/Dropdown';
import Icon from '@/componentsDS/Icon/Icon';
import Input from '@/componentsDS/Input';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';

// Control characters, so an option label containing the delimiter cannot make two
// different option lists produce the same signature.
const OPTION_FIELD_SEPARATOR = '\u001f'; // ASCII unit separator
const OPTION_SEPARATOR = '\u001e'; // ASCII record separator

const filterOptionsByText = (options, text) => {
	if (!text) {
		return options;
	}

	const searchText = text.toLowerCase();
	return options.filter((option) => option.label.toLowerCase().includes(searchText));
};

const InputDropdown = ({ id, name, label, labelInfoText, variant, size, fullWidth = false, required, disabled, readOnly = false, infoText, errorMessage, captionText, value, onChange, dropdownOption, dropdownSelectedOption, onSelectDropdownItem, multipleSelection = false, allowSelectAll, isLoading, showTooltipInfo = false, placeholder, dataTestId }) => {
	const [dropdownWidth, setDropdownWidth] = useState();
	const [filteredOptions, setFilteredOptions] = useState(dropdownOption);
	const [isClearing, setIsClearing] = useState(false);
	const [chipValues, setChipValues] = useState([]);
	const [isOpen, setIsOpen] = useState(false);
	const [noResults, setNoResults] = useState(false);
	const [inputText, setInputText] = useState(value || '');
	const inputRef = useRef(null);
	// inputText is not always a search string: it also holds the pre-filled `value`
	// a caller mounts with, and the label written back after a single selection.
	// Only text the user actually typed into the search box may narrow the list.
	const hasSearchText = useRef(false);

	// Parents rebuild the option array on every render, so key the re-sync on the
	// option contents to avoid re-filtering when nothing actually changed.
	const optionsSignature = useMemo(() => dropdownOption.map((option) => `${option?.id}${OPTION_FIELD_SEPARATOR}${option?.label}`).join(OPTION_SEPARATOR), [dropdownOption]);

	// Options can arrive after mount (slow API) or while the dropdown is open —
	// re-derive the visible list instead of keeping the snapshot taken at mount.
	// Dropdown.jsx:304-306 reads filteredOptions purely as an id set to intersect
	// with its own copy of the options, so this list only has to carry ids.
	useEffect(() => {
		const searchText = hasSearchText.current ? inputText : '';
		const nextOptions = filterOptionsByText(dropdownOption, searchText);
		setFilteredOptions(nextOptions);
		// Only meaningful once the user has typed: an empty list on its own is a
		// still-loading list, not a search miss.
		setNoResults(Boolean(searchText) && nextOptions.length === 0);
	}, [optionsSignature]);

	// Measure the width of the Input component to set the dropdown width dynamically with the input width
	useEffect(() => {
		if (inputRef.current) {
			const inputElement = inputRef.current.querySelector('.DS__input')
			if (inputElement) {
				setDropdownWidth(inputElement.offsetWidth); // Measure the `.DS__input` width
			}
		}
	}, [fullWidth, isOpen]);

	useEffect(() => {
		const nextText = value || '';
		// Callers whose onChange handler takes the raw string (signup company-info.jsx,
		// signup appointments.jsx, onboarding service-provider.jsx) feed it straight
		// back into `value`, echoing the typed text back as a prop: that is still the
		// user's search, so leave it alone. This relies on the echo being synchronous —
		// onChange, the caller's setState and this render batch together, so `value`
		// lands alongside the `inputText` it came from. A debounced echo would arrive
		// after inputText moved on and would be read as a parent push instead.
		if (nextText === inputText) {
			return;
		}

		// Anything else the parent pushes in is a selection, not a search, so the
		// search state it lands on has to go with the text it replaces.
		hasSearchText.current = false;
		setInputText(nextText);
		setNoResults(false);
		setFilteredOptions(dropdownOption);
	}, [value]);

	useEffect(() => {
		if (!isOpen && multipleSelection) {
			hasSearchText.current = false;
			// The search text is being wiped here, so the search miss it produced
			// has to go with it — otherwise reopening shows "No results found."
			// over an empty panel with nothing left in the box to explain it.
			setNoResults(false);
			setInputText('');
			setFilteredOptions(dropdownOption);
			onChange('');
		}
	}, [isOpen]);

	useEffect(() => {
		if (multipleSelection) {
			const selectedValue = dropdownSelectedOption.map((item) => ({
				...item,
				id: String(item.id),
				label: item.label
			}));
			setChipValues(selectedValue);
		}
	}, [dropdownSelectedOption, multipleSelection]);

	const handleInputChange = (newVal) => {
		hasSearchText.current = newVal !== '';
		setInputText(newVal);
		onChange(newVal); // Pass typed value to parent

		const filtered = filterOptionsByText(dropdownOption, newVal);

		if (filtered.length === 0) {
			setNoResults(true);
		} else {
			setNoResults(false);
		}

		setFilteredOptions(filtered);

		if (newVal === '') {
			setIsClearing(true);
		}

		if (newVal === '' && !multipleSelection) {
			onSelectDropdownItem([]);
		}
	};

	const handleDropdownSelect = (selectedItems) => {
		if (isClearing) {
			setIsClearing(false); // Stop clearing once selection is changed
			if (!isOpen) return; // Prevent infinite re-rendering when dropdown is closed
		}

		onSelectDropdownItem(selectedItems);

		if (!multipleSelection) {
			// The chosen label goes back into the input as an echo, not as a search.
			hasSearchText.current = false;
			setFilteredOptions(dropdownOption);
			const selectedValue = selectedItems[0]?.label || '';
			setInputText(selectedValue);
			onChange(selectedValue);
		} else {
			const selectedValue = selectedItems.map((item) => ({
				...item,
				id: String(item.id),
				label: item.label
			}));
			setChipValues(selectedValue);

			// Clear the search text once it has served its purpose (narrowing results to pick from).
			hasSearchText.current = false;
			setInputText('');
			onChange('');
			setFilteredOptions(dropdownOption);
		}
	};

	const handleClearChipSelection = () => {
		setIsClearing(true);
		setChipValues([]);
		onSelectDropdownItem([]);
		setFilteredOptions(dropdownOption);
		handleInputChange('');
		setIsOpen(false);
	};

	const onInputClick = () => {
		setIsOpen(true);
	};

	const suffixIcon = <Icon name="chevron-down" style="solid" size="small" />;

	const DropdownTrigger = (
		<div ref={inputRef}>
			<Input
				id={id}
				name={name}
				placeholder={placeholder || 'Search'}
				suffixIcon={suffixIcon}
				label={label}
				labelInfoText={labelInfoText}
				variant={variant}
				size={size}
				required={required}
				disabled={disabled}
				readOnly={readOnly}
				infoText={infoText}
				errorMessage={errorMessage}
				captionText={captionText}
				value={inputText}
				onChange={handleInputChange}
				onClick={onInputClick}
				chipInputMultiple={multipleSelection}
				chipValues={multipleSelection ? chipValues : undefined}
				setChipValues={setChipValues}
				onClearChips={handleClearChipSelection}
				fullWidth={fullWidth}
			/>
		</div>
	);

	return (
		<div style={{ width: fullWidth ? '100%' : '350px' }}>
			<Dropdown
				dataTestId={dataTestId}
				options={dropdownOption}
				filteredOptions={filteredOptions}
				value={dropdownSelectedOption}
				onAdd={handleDropdownSelect}
				trigger={DropdownTrigger}
				multiple={multipleSelection}
				allowSelectAll={allowSelectAll}
				isOpen={isOpen}
				setIsOpen={setIsOpen}
				size={size}
				disabled={disabled}
				setWidth={dropdownWidth}
				isLoading={isLoading}
				noResults={noResults}
				fullWidth={fullWidth}
				showTooltipInfo={showTooltipInfo}
			/>
		</div>
	);
};

export default InputDropdown;

InputDropdown.propTypes = {
	id: PropTypes.string,
	name: PropTypes.string,
	label: PropTypes.string,
	labelInfoText: PropTypes.string,
	variant: PropTypes.oneOf(['outlined', 'filled', 'underlined']),
	size: PropTypes.oneOf(['default', 'small']),
	fullWidth: PropTypes.bool,
	required: PropTypes.bool,
	disabled: PropTypes.bool,
	readOnly: PropTypes.bool,
	infoText: PropTypes.string,
	errorMessage: PropTypes.string,
	captionText: PropTypes.string,
	value: PropTypes.string,
	onChange: PropTypes.func,
	dropdownOption: PropTypes.array,
	dropdownSelectedOption: PropTypes.array,
	onSelectDropdownItem: PropTypes.func,
	multipleSelection: PropTypes.bool,
	allowSelectAll: PropTypes.bool,
	isLoading: PropTypes.bool,
	placeholder: PropTypes.string,
	showTooltipInfo: PropTypes.bool,
	dataTestId: PropTypes.string
};
